import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import * as React from "react";
import LeftChevron from "components/common/LeftChevron";
import {
  CTABorderStyle,
  CTAPosition,
  InsertCallToActionPayload,
} from "../plugins/CallToActionPlugin";

type CallToActionComponentProps = Readonly<
  {
    nodeKey: NodeKey;
    borderRadius: string;
  } & InsertCallToActionPayload
>;

const ABIN_EDITOR_CTA = 'abin-editor-cta';
const ABIN_EDITOR_CTA_TITLE = 'abin-editor-cta-title';
const ABIN_EDITOR_CTA_PRICE_TITLE = 'abin-editor-cta-price-title';

export const DATA_CTA_IMG = 'data-cta-img';
export const DATA_CTA_MOBILE_IMG = 'data-cta-mobile-img';
export const DATA_CTA_ACTION_LINK = 'data-cta-action-link';
export const DATA_CTA_ACTION_TEXT = 'data-cta-action-text';
export const DATA_CTA_ACTION_TEXT_COLOR = 'data-cta-action-text-color';
export const DATA_CTA_ACTION_TEXT_SIZE = 'data-cta-action-text-size';
export const DATA_CTA_ACTION_BUTTON_COLOR = 'data-cta-action-button-color';
export const DATA_CTA_ACTION_BUTTON_SIZE = 'data-cta-action-button-size';
export const DATA_CTA_ACTION_POSITION = 'data-cta-action-position';
export const DATA_CTA_BORDER_STYLE = 'data-cta-border-style';
export const DATA_CTA_PRICE_SHOW = 'data-cta-price-show';
export const DATA_CTA_PRICE_TEXT_COLOR = 'data-cta-price-text-color';
export const DATA_CTA_PRICE_TEXT_SIZE = 'data-cta-price-text-size';
export const DATA_CTA_PRICE_H_POSITION = 'data-cta-price-h-position';
export const DATA_CTA_PRICE_V_POSITION = 'data-cta-price-v-position';

export function CallToActionComponent({
  nodeKey,
  imgAddress,
  mobileImgAddress,
  actionLink,
  actionText,
  actionTextColor,
  actionTextSize,
  actionButtonColor,
  actionButtonSize,
  actionPosition,
  borderRadius,
  showPrice,
  priceTextColor,
  priceTextSize,
  priceHPosition,
  priceVPosition
}: CallToActionComponentProps) {
  return (
    <a
      key={nodeKey}
      style={{
        width: "100%",
        height: "100px",
        position: "relative",
        color: "black",
        display: "block",
      }}
      href={actionLink}
    >
      <img
        src={imgAddress}
        className="d-md-block d-none"
        style={{
          objectFit: "cover",
          width: "100%",
          height: "auto",
          minHeight: "100%",
          maxHeight: "100%",
          maxWidth: "100%",
          borderRadius: borderRadius,
        }}
      />
      <img
        src={mobileImgAddress}
        className="d-md-none d-sm-block"
        style={{
          objectFit: "cover",
          width: "100%",
          height: "auto",
          minHeight: "100%",
          maxHeight: "100%",
          maxWidth: "100%",
          borderRadius: borderRadius,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: actionPosition,
          gap: "0.5rem",
          paddingInline: actionPosition === "center" ? "20vw" : "1rem",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            width: "50%",
            color: actionTextColor,
            fontSize: `${actionTextSize}px`,
            textAlign: actionPosition,
          }}
        >
          {actionText}
        </span>
        <LeftChevron
          color={actionButtonColor}
          size={`${actionButtonSize}px`}
        />
      </div>
      {showPrice && (
        <div
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            display: "flex",
            alignItems: priceVPosition,
            justifyContent: priceHPosition,
            padding: '0.5rem 1rem 0.5rem 1rem',
            overflow: "hidden",
          }}
        >
          <span
            style={{
              // width: "50%",
              color: priceTextColor,
              fontSize: `${priceTextSize}px`,
              textAlign: priceHPosition,
            }}
          >
            محل نمایش قیمت
          </span>
        </div>
      )}
    </a>
  );
}

export type SerializedCallToActionNode = Spread<
  InsertCallToActionPayload,
  SerializedDecoratorBlockNode
>;

function $convertCallToActionElement(
  domNode: HTMLElement
): null | DOMConversionOutput {
  const imgAddress = domNode.getAttribute(DATA_CTA_IMG);
  const mobileImgAddress = domNode.getAttribute(DATA_CTA_MOBILE_IMG);
  const actionLink = domNode.getAttribute(DATA_CTA_ACTION_LINK) ?? "";
  const actionText = domNode.getAttribute(DATA_CTA_ACTION_TEXT) ?? "";
  const actionTextColor =
    domNode.getAttribute(DATA_CTA_ACTION_TEXT_COLOR) ?? "";
  const actionTextSize =
    domNode.getAttribute(DATA_CTA_ACTION_TEXT_SIZE) ?? "";
  const actionButtonColor =
    domNode.getAttribute(DATA_CTA_ACTION_BUTTON_COLOR) ?? "";
  const actionButtonSize =
    domNode.getAttribute(DATA_CTA_ACTION_BUTTON_SIZE) ?? "";
  const actionPosition = domNode.getAttribute(DATA_CTA_ACTION_POSITION) ?? "";
  const borderStyle = domNode.getAttribute(DATA_CTA_BORDER_STYLE) ?? "";
  const showPrice =
    (domNode.getAttribute(DATA_CTA_PRICE_SHOW) ?? "false") == 'true';
  const priceTextColor =
    domNode.getAttribute(DATA_CTA_PRICE_TEXT_COLOR) ?? "";
  const priceTextSize =
    domNode.getAttribute(DATA_CTA_PRICE_TEXT_SIZE) ?? "";
  const priceHPosition =
    domNode.getAttribute(DATA_CTA_PRICE_H_POSITION) ?? "";
  const priceVPosition =
    domNode.getAttribute(DATA_CTA_PRICE_V_POSITION) ?? "";

  if (imgAddress) {
    const node = $createCallToActionNode({
      imgAddress,
      mobileImgAddress: mobileImgAddress ?? imgAddress,
      actionLink,
      actionText,
      actionTextColor,
      actionTextSize,
      actionButtonColor,
      actionButtonSize,
      actionPosition: actionPosition as CTAPosition,
      borderStyle: borderStyle as CTABorderStyle,
      showPrice: showPrice,
      priceTextColor: priceTextColor,
      priceTextSize: priceTextSize,
      priceHPosition: priceHPosition as CTAPosition,
      priceVPosition: priceVPosition as CTAPosition
    });
    return { node };
  }

  return null;
}

export class CallToActionNode extends DecoratorBlockNode {
  __imgAddress: string;
  __mobileImgAddress: string;
  __actionLink: string;
  __actionText: string;
  __actionTextColor: string;
  __actionTextSize: string;
  __actionButtonColor: string;
  __actionButtonSize: string;
  __borderStyle: CTABorderStyle;
  __actionPosition: string;
  __showPrice: boolean;
  __priceTextColor: string;
  __priceTextSize: string;
  __priceHPosition: CTAPosition;
  __priceVPosition: CTAPosition;

  constructor(
    imgAddress: string,
    mobileImgAddress: string,
    actionLink: string,
    actionText: string,
    actionTextColor: string,
    actionTextSize: string,
    actionButtonColor: string,
    actionButtonSize: string,
    actionPosition: string,
    borderStyle: CTABorderStyle,
    showPrice: boolean,
    priceTextColor: string,
    priceTextSize: string,
    priceHPosition: CTAPosition,
    priceVPosition: CTAPosition,
    format?: ElementFormatType,
    key?: NodeKey
  ) {
    super(format, key);

    this.__imgAddress = imgAddress;
    this.__mobileImgAddress = mobileImgAddress;
    this.__actionLink = actionLink;
    this.__actionText = actionText;
    this.__actionTextColor = actionTextColor;
    this.__actionTextSize = actionTextSize;
    this.__actionButtonColor = actionButtonColor;
    this.__actionButtonSize = actionButtonSize;
    this.__actionPosition = actionPosition;
    this.__borderStyle = borderStyle;
    this.__showPrice = showPrice;
    this.__priceTextColor = priceTextColor;
    this.__priceTextSize = priceTextSize;
    this.__priceHPosition = priceHPosition;
    this.__priceVPosition = priceVPosition;
  }

  static getType(): string {
    return "call-to-action";
  }

  static clone(node: CallToActionNode): CallToActionNode {
    return new CallToActionNode(
      node.__imgAddress,
      node.__mobileImgAddress,
      node.__actionLink,
      node.__actionText,
      node.__actionTextColor,
      node.__actionTextSize,
      node.__actionButtonColor,
      node.__actionButtonSize,
      node.__actionPosition,
      node.__borderStyle,
      node.__showPrice,
      node.__priceTextColor,
      node.__priceTextSize,
      node.__priceHPosition,
      node.__priceVPosition,
      node.__format,
      node.__key
    );
  }

  static importJSON(
    serializedNode: SerializedCallToActionNode
  ): CallToActionNode {
    const node = $createCallToActionNode({
      imgAddress: serializedNode.imgAddress,
      mobileImgAddress: serializedNode.mobileImgAddress,
      actionLink: serializedNode.actionLink,
      actionText: serializedNode.actionText,
      actionTextColor: serializedNode.actionTextColor,
      actionTextSize: serializedNode.actionTextSize,
      actionButtonColor: serializedNode.actionButtonColor,
      actionButtonSize: serializedNode.actionButtonSize,
      actionPosition: serializedNode.actionPosition,
      borderStyle: serializedNode.borderStyle,
      showPrice: serializedNode.showPrice,
      priceTextColor: serializedNode.priceTextColor,
      priceTextSize: serializedNode.priceTextSize,
      priceHPosition: serializedNode.priceHPosition,
      priceVPosition: serializedNode.priceVPosition
    });
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedCallToActionNode {
    return {
      ...super.exportJSON(),
      type: "call-to-action",
      version: 1,
      imgAddress: this.__imgAddress,
      mobileImgAddress: this.__mobileImgAddress,
      actionLink: this.__actionLink,
      actionText: this.__actionText,
      actionTextColor: this.__actionTextColor,
      actionTextSize: this.__actionTextSize,
      actionButtonColor: this.__actionButtonColor,
      actionButtonSize: this.__actionButtonSize,
      actionPosition: this.__actionPosition as CTAPosition,
      borderStyle: this.__borderStyle,
      showPrice: this.__showPrice,
      priceTextColor: this.__priceTextColor,
      priceTextSize: this.__priceTextSize,
      priceHPosition: this.__priceHPosition,
      priceVPosition: this.__priceVPosition
    };
  }

  getBorderRadius() {
    switch (this.__borderStyle) {
      case "flat":
        return "0";
      case "rounded":
        return "20px";
      default:
        return "12px";
    }
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement("a");
    container.className = ABIN_EDITOR_CTA;
    container.setAttribute(DATA_CTA_IMG, this.__imgAddress);
    container.setAttribute(DATA_CTA_MOBILE_IMG, this.__mobileImgAddress);
    container.setAttribute(DATA_CTA_ACTION_LINK, this.__actionLink);
    container.setAttribute(DATA_CTA_ACTION_TEXT, this.__actionText);
    container.setAttribute(
      DATA_CTA_ACTION_TEXT_COLOR,
      this.__actionTextColor
    );
    container.setAttribute(DATA_CTA_ACTION_TEXT_SIZE, this.__actionTextSize);
    container.setAttribute(
      DATA_CTA_ACTION_BUTTON_COLOR,
      this.__actionButtonColor
    );
    container.setAttribute(DATA_CTA_ACTION_BUTTON_SIZE, this.__actionButtonSize);
    container.setAttribute(DATA_CTA_ACTION_POSITION, this.__actionPosition);
    container.setAttribute(DATA_CTA_BORDER_STYLE, this.__borderStyle);
    container.setAttribute(DATA_CTA_PRICE_SHOW, `${this.__showPrice}`);
    container.setAttribute(DATA_CTA_PRICE_TEXT_COLOR, this.__priceTextColor);
    container.setAttribute(DATA_CTA_PRICE_TEXT_SIZE, this.__priceTextSize);
    container.setAttribute(DATA_CTA_PRICE_H_POSITION, this.__priceHPosition);
    container.setAttribute(DATA_CTA_PRICE_V_POSITION, this.__priceVPosition);
    container.href = this.__actionLink;
    Object.assign(container.style, {
      width: "100%",
      height: "100px",
      position: "relative",
      color: "black",
      display: "block",
      textDecoration: "none",
    });

    const img = document.createElement("img");
    img.src = this.__imgAddress;
    img.className = 'd-md-block d-none';
    Object.assign(img.style, {
      objectFit: "cover",
      width: "100%",
      height: "auto",
      minHeight: "100%",
      maxHeight: "100%",
      maxWidth: "100%",
      borderRadius: this.getBorderRadius(),
    });

    const mobileImg = document.createElement("img");
    mobileImg.src = this.__mobileImgAddress;
    mobileImg.className = 'd-md-none d-sm-block';
    Object.assign(mobileImg.style, {
      objectFit: "cover",
      width: "100%",
      height: "auto",
      minHeight: "100%",
      maxHeight: "100%",
      maxWidth: "100%",
      borderRadius: this.getBorderRadius(),
    });

    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: this.__actionPosition,
      gap: "0.5rem",
      paddingInline: this.__actionPosition === "center" ? "20vw" : "1rem",
      overflow: "hidden",
    });

    const title = document.createElement("span");
    title.textContent = this.__actionText;
    title.style.width = "50%";
    title.style.textAlign = this.__actionPosition;
    title.style.color = this.__actionTextColor;
    title.style.fontSize = `${this.__actionTextSize}px`;
    title.className = ABIN_EDITOR_CTA_TITLE;

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", `${this.__actionButtonSize}px`);
    svg.setAttribute("height", `${this.__actionButtonSize}px`);
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M15 18l-6-6 6-6");
    path.setAttribute("stroke", this.__actionButtonColor);
    path.setAttribute("stroke-width", "2");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.appendChild(path);

    const priceContainer = document.createElement("div");
    if (this.__showPrice) {
      Object.assign(priceContainer.style, {
        position: "absolute",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        display: "flex",
        alignItems: this.__priceVPosition,
        justifyContent: this.__priceHPosition,
        padding: '0.5rem 1rem 0.5rem 1rem',
        overflow: "hidden",
      });

      const priceTitle = document.createElement("span");
      priceTitle.textContent = "";
      priceTitle.style.textAlign = this.__priceHPosition;
      priceTitle.style.color = this.__priceTextColor;
      priceTitle.style.fontSize = `${this.__priceTextSize}px`;
      priceTitle.className = ABIN_EDITOR_CTA_PRICE_TITLE;

      priceContainer.appendChild(priceTitle);
    }

    // compose
    overlay.appendChild(title);
    overlay.appendChild(svg);
    container.appendChild(img);
    container.appendChild(mobileImg);
    container.appendChild(overlay);
    if (this.__showPrice) container.appendChild(priceContainer);

    return { element: container };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute(DATA_CTA_IMG)) {
          return null;
        }
        return {
          conversion: $convertCallToActionElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    return (
      <CallToActionComponent
        nodeKey={this.getKey()}
        imgAddress={this.__imgAddress}
        mobileImgAddress={this.__mobileImgAddress}
        actionLink={this.__actionLink}
        actionText={this.__actionText}
        actionTextColor={this.__actionTextColor}
        actionTextSize={this.__actionTextSize}
        actionButtonColor={this.__actionButtonColor}
        actionButtonSize={this.__actionButtonSize}
        actionPosition={this.__actionPosition as CTAPosition}
        borderRadius={this.getBorderRadius()}
        borderStyle={this.__borderStyle}
        showPrice={this.__showPrice}
        priceTextColor={this.__priceTextColor}
        priceTextSize={this.__priceTextSize}
        priceHPosition={this.__priceHPosition}
        priceVPosition={this.__priceVPosition}
      />
    );
  }
}

export function $createCallToActionNode(
  payload: InsertCallToActionPayload
): CallToActionNode {
  return new CallToActionNode(
    payload.imgAddress,
    payload.mobileImgAddress,
    payload.actionLink,
    payload.actionText,
    payload.actionTextColor,
    payload.actionTextSize,
    payload.actionButtonColor,
    payload.actionButtonSize,
    payload.actionPosition,
    payload.borderStyle,
    payload.showPrice,
    payload.priceTextColor,
    payload.priceTextSize,
    payload.priceHPosition,
    payload.priceVPosition
  );
}

export function $isCallToActionNode(
  node: CallToActionNode | LexicalNode | null | undefined
): node is CallToActionNode {
  return node instanceof CallToActionNode;
}
